import type { Client } from '@rhitm-incubator/oidc-manager';
import { PRODUCTS } from './data/products.data.js';

type IsoDateString = `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;

export type ProductName = keyof typeof PRODUCTS;

export type ProductPhaseName = typeof PRODUCTS[keyof typeof PRODUCTS]['Full Support' | 'Maintenance Phase' | 'ELS Phase'];

/**
 * Ordinal Enum / Ordinal Mapping
 *
 * Allows us to have sort order of an enum.
 */
export const SUPPORT_STATES = {
  FULL_SUPPORT: 0,
  MAINTENANCE_WINDOW: 1,
  ELS_WINDOW: 2,
  NO_SUPPORT: 3
} as const;

/**
 * The raw JSON response from the GraphQL API
 */
export interface ProductRaw {
  id: string;
  name: ProductName;
  version: string;
  phases: Record<ProductPhaseName, IsoDateString | undefined | 'N/A'>;
}

export interface Product extends ProductRaw {
  /**
   * Full Support date based on product phases.
   */
  fullSupportDate: IsoDateString | null;
  /**
   * The calculated terminal maintenance support date
   * based on product phases.
   */
  maintenanceDate: IsoDateString | null;
  /**
   * The calculated terminal els support date
   * based on product phases.
   */
  elsDate: IsoDateString | null;
  /**
   * The products els url based on product name.
   */
  elsUrl: string | null;
  /**
   * The calculated deterministic state of the
   * product support based on mainenance and elsDate
   */
  supportState: typeof SUPPORT_STATES[keyof typeof SUPPORT_STATES];
}

export class ProductListService {
  private static instance: ProductListService;

  private inFlightRequest: Promise<Product[]> | null = null;

  public products?: Product[];

  constructor(public graphqlEndpoint: string, public user: NonNullable<Client['user']>) { }

  /**
   * Get the singleton instance of ProductListService
   */
  public static getInstance(graphqlEndpoint: string, user: NonNullable<Client['user']>): ProductListService {
    if (!ProductListService.instance) {
      ProductListService.instance = new ProductListService(graphqlEndpoint, user);
    }
    return ProductListService.instance;
  }

  /**
   * Fetches a list of products from the graphql service
   *
   * If multiple calls are made in the same task queue, they will share the same promise.
   * This prevents duplicate network requests.
   *
   * @example
   * const productList = productListService;
   * productListService.getProductList();
   */
  async getProductList(): Promise<Product[]> {
    // If there's an in-flight request, return it
    if (this.inFlightRequest) {
      return this.inFlightRequest;
    }

    // Create new request
    this.inFlightRequest = this._fetchProducts();

    try {
      const products = await this.inFlightRequest;
      return products;
    } finally {
      // Clear the in-flight request when done
      this.inFlightRequest = null;
    }
  }

  /**
   * Internal method to fetch products from the API
   */
  private async _fetchProducts(): Promise<Product[]> {
    const response = await fetch(this.graphqlEndpoint, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'Authorization': `Bearer ${this.user.access_token}`,
        'apollographql-client-name': 'my-red-hat',
        'apollographql-client-version': 'latest'
      },
      body: JSON.stringify({
        query: `
        query Products($names: [String!]!) {
            products(names: $names) {
              name
              lifecycle {
                versions {
                  edges {
                    node {
                      lastMinorRelease
                      phases {
                        name
                        date
                      }
                      name
                    }
                  }
                }
              }
            }
          }
        `,
        variables: { names: ['Red Hat Enterprise Linux', 'OpenShift Container Platform 4', 'Red Hat Ansible Automation Platform'] }
      })
    });
    const data = await response.json();
    if (data.errors) {
      throw new Error(data.errors.map((error: any) => error.message).join(', '));
    }

    const products: Product[] = [];

    // convert raw data to Product type
    for (const product of data.data.products) {
      const name = product.name.replace('OpenShift Container Platform 4', 'Red Hat OpenShift Container Platform') as ProductName;
      for (const lifecycle of product.lifecycle.versions.edges) {
        const version = lifecycle.node.name;
        const phases = Object.fromEntries(lifecycle.node.phases.map(({ name, date }: any) => {
          // Format the phase name to a machine readable name
          const _name = `${name}`.split(' ').join('_').toUpperCase();
          return [_name, date];
        })) as Product['phases'];

        const productRaw = {
          id: `${name}:${version}`,
          name,
          version,
          phases
        } as Product;

        this._setFullSupportDate(productRaw);
        this._setMaintenanceDate(productRaw);
        this._setElsDate(productRaw);
        this._setSupportState(productRaw);
        this._setElsUrl(productRaw);

        products.push(productRaw);
      }
    }

    // sort by semver version
    products.sort((a, b) => {
      const versionA = a.version.replace(/^v/, '').split('.').map(Number); // Remove 'v' and split by '.'
      const versionB = b.version.replace(/^v/, '').split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if (versionA[i] !== versionB[i]) {
          return versionB[i] - versionA[i]; // Compare each segment: MAJOR, MINOR, PATCH
        }
      }
      return 0; // If they are identical
    });

    this.products = products;
    return products;
  }

  /**
   * Helpers
   */
  /**
   * Calculates and sets maintenance support date based on product phases.
   */
  _setFullSupportDate(product: Product): void {
    const phaseName = PRODUCTS[product.name]['Full Support'];
    const phaseDate = phaseName && product.phases?.[phaseName];

    product.fullSupportDate = phaseDate && phaseDate !== 'N/A' ? phaseDate : null;
  }

  /**
   * Calculates and sets maintenance support date based on product phases.
   */
  _setMaintenanceDate(product: Product): void {
    const phaseName = PRODUCTS[product.name]['Maintenance Phase'];
    const phaseDate = phaseName && product.phases?.[phaseName];

    product.maintenanceDate = phaseDate && phaseDate !== 'N/A' ? phaseDate : null;
  }

  /**
   * Calculates and sets ELS support date based on product phases.
   * Finds the nearst future els date.
   */
  _setElsDate(product: Product): void {
    const phaseName = PRODUCTS[product.name]['ELS Phase'];
    const phaseDate = phaseName && product.phases?.[phaseName];
    const currentDate = new Date().toISOString();

    product.elsDate = phaseDate && phaseDate !== 'N/A' && phaseDate > currentDate ? phaseDate : null;
  }

  /**
   * Calculates and sets the support state based on maintenance and ELS dates.
   */
  _setSupportState(product: Product): void {
    const currentDate = new Date().toISOString();
    let supportState: Product['supportState'] = SUPPORT_STATES.NO_SUPPORT;

    if (!!product.fullSupportDate && currentDate < product.fullSupportDate) {
      supportState = SUPPORT_STATES.FULL_SUPPORT;
    } else if (!!product.maintenanceDate && currentDate < product.maintenanceDate) {
      supportState = SUPPORT_STATES.MAINTENANCE_WINDOW;
    } else if (!!product.elsDate && currentDate < product.elsDate) {
      supportState = SUPPORT_STATES.ELS_WINDOW;
    }

    product.supportState = supportState;
  }

  /**
   * Sets the elsUrl for this product.
   */
  _setElsUrl(product: Product): void {
    product.elsUrl = PRODUCTS[product.name]['ELS URL'];
  }
}
